const STAGE_RANK = {
  new: 0,
  contacting: 1,
  engaged: 2,
  appraisal: 3,
  offer: 4,
  appointment: 5,
  acquired: 6
};

const normalizeStage = (customer) => {
  const raw = String(customer?.acquisitionStatus || customer?.status || '').toLowerCase().trim();
  if (raw === 'follow-up' || raw === 'follow up' || raw === 'followup') return 'follow-up';
  if (raw === 'lost' || raw.includes('declined') || raw.includes('dead') || raw === 'not-interested') return 'lost';
  if (raw === 'acquired' || raw.includes('purchased') || raw.includes('completed')) return 'acquired';
  if (raw.includes('appointment')) return 'appointment';
  if (raw.includes('offer')) return 'offer';
  if (raw.includes('apprais')) return 'appraisal';
  if (raw.includes('hot') || raw.includes('interest') || customer?.tradeInInterest) return 'engaged';
  if (raw.includes('contact') || raw.includes('messag') || raw.includes('conversation')) return 'contacting';
  return 'new';
};

const storedStageFor = stage => ({
  lost: 'not-interested',
  appointment: 'appointment-set',
  engaged: 'hot-opportunity',
  contacting: 'in-conversation'
}[stage] || stage);

const latestInboundMessage = (customer) => {
  const conversations = Array.isArray(customer?.conversations) ? customer.conversations : [];
  return [...conversations]
    .reverse()
    .find(message => String(message?.sender || '').toLowerCase() === 'customer' && message?.message);
};

const dateFromPhrase = (text) => {
  const now = new Date();
  const result = new Date(now);

  if (/\btomorrow\b/i.test(text)) {
    result.setDate(result.getDate() + 1);
  } else if (/\bnext week\b/i.test(text)) {
    result.setDate(result.getDate() + 7);
  } else if (/\bnext month\b/i.test(text)) {
    result.setMonth(result.getMonth() + 1);
  } else if (/\bin (\d{1,2}) days?\b/i.test(text)) {
    const match = text.match(/\bin (\d{1,2}) days?\b/i);
    result.setDate(result.getDate() + Number(match?.[1] || 1));
  } else {
    return '';
  }

  return result.toISOString().split('T')[0];
};

const shouldAdvanceTo = (currentStage, nextStage) => {
  if (currentStage === 'acquired') return false;
  if (nextStage === 'lost' || nextStage === 'follow-up') return true;
  if (currentStage === 'lost' || currentStage === 'follow-up') return true;
  return (STAGE_RANK[nextStage] ?? -1) > (STAGE_RANK[currentStage] ?? -1);
};

const appendActivity = (customer, event) => {
  const existing = Array.isArray(customer?.acquisitionActivity) ? customer.acquisitionActivity : [];
  const id = event.id || `sydney-${event.timestamp}-${event.toStage || event.type}`;
  if (existing.some(item => item.id === id)) return existing;
  return [...existing, { ...event, id }].slice(-250);
};

export const buildSydneyPipelineUpdate = (customer) => {
  if (!customer?.id) return null;

  const inbound = latestInboundMessage(customer);
  if (!inbound?.message) return null;

  const messageAt = inbound.timestamp || inbound.createdAt || '';
  if (messageAt && customer.sydneyPipelineLastMessageAt === messageAt) return null;

  const text = String(inbound.message).trim().toLowerCase();
  const currentStage = normalizeStage(customer);
  let nextStage = null;
  let reason = '';
  const updates = {};

  const optOut = /(^|\b)(stop|unsubscribe|remove me|take me off|do not contact|don['’]?t contact|leave me alone)(\b|$)/i.test(text);
  const decline = optOut || /(not interested|no thanks|no thank you|stop texting|stop messaging)/i.test(text);
  const later = /(maybe later|not right now|not now|call me later|text me later|follow up|next week|next month|circle back|reach back out|try me later|tomorrow works better|call me tomorrow|text me tomorrow)/i.test(text);
  const appointment = /(come in|stop by|bring (it|the car|vehicle) in|set (an )?appointment|schedule (an )?appointment|book (an )?appointment|i can be there|i['’]?ll be there|see you (today|tomorrow)|tomorrow at|today at)/i.test(text);
  const appraisal = /(what(?:'s| is) (it|my car|my vehicle) worth|how much (is it worth|would you give|can you give|will you give)|what would you give|value my|apprais(e|al)|what(?:'s| is) your offer|make me an offer|offer me)/i.test(text);
  const engaged = /(i['’]?d sell|i would sell|would consider selling|consider selling|want to sell|looking to sell|trade it|trade my|interested in selling|interested in trading|might sell|may sell|yes.*sell|yes.*trade)/i.test(text);

  if (decline) {
    nextStage = 'lost';
    reason = optOut ? 'Customer opted out by text' : 'Customer declined by text';
    updates.lostReason = customer.lostReason || reason;
    updates.nextAction = '';
    updates.priority = 'low';
    if (optOut) updates.smsOptOut = true;
  } else if (later) {
    nextStage = 'follow-up';
    reason = 'Customer asked for follow-up';
    updates.followUpDate = dateFromPhrase(text) || customer.followUpDate || '';
    updates.nextAction = updates.followUpDate ? `Follow up on ${updates.followUpDate}` : 'Follow up with customer';
  } else if (appointment) {
    nextStage = 'appointment';
    reason = 'Customer indicated appointment intent';
    updates.nextAction = 'Confirm appraisal appointment';
    updates.priority = 'urgent';
  } else if (appraisal) {
    nextStage = 'appraisal';
    reason = 'Customer asked about vehicle value';
    updates.nextAction = 'Appraise vehicle and prepare offer';
  } else if (engaged) {
    nextStage = 'engaged';
    reason = 'Customer expressed selling or trade interest';
    updates.nextAction = 'Review vehicle and contact customer';
    updates.tradeInInterest = true;
    updates.priority = 'high';
  } else if (currentStage === 'new') {
    nextStage = 'contacting';
    reason = 'Customer replied';
    updates.nextAction = customer.nextAction || 'Continue acquisition conversation';
  }

  const legacyRaw = String(customer.acquisitionStatus || '').toLowerCase();
  if (!nextStage && legacyRaw.includes('offer')) {
    nextStage = 'offer';
    reason = 'Sydney generated an offer';
    updates.nextAction = customer.nextAction || 'Review offer response';
  }

  const now = new Date().toISOString();

  if (!nextStage || !shouldAdvanceTo(currentStage, nextStage)) {
    if (!messageAt || customer.sydneyPipelineLastMessageAt === messageAt) return null;
    return {
      sydneyPipelineLastMessageAt: messageAt,
      sydneyPipelineReason: customer.sydneyPipelineReason || 'Latest customer reply reviewed',
      sydneyPipelineUpdatedAt: now
    };
  }

  const activity = appendActivity(customer, {
    id: `sydney-${messageAt || now}-${nextStage}`,
    type: 'stage-change',
    actor: 'Sydney AI',
    source: 'automation',
    timestamp: now,
    fromStage: currentStage,
    toStage: nextStage,
    title: `Sydney moved opportunity to ${nextStage}`,
    detail: reason,
    relatedMessageAt: messageAt,
    relatedMessage: String(inbound.message).slice(0, 500)
  });

  return {
    acquisitionStatus: storedStageFor(nextStage),
    ...updates,
    acquisitionActivity: activity,
    sydneyPipelineAutomated: true,
    sydneyPipelineReason: reason,
    sydneyPipelineLastMessageAt: messageAt,
    sydneyPipelineUpdatedAt: now
  };
};
